import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Heart, PawPrint, Sparkles } from "lucide-react";
import { api, MEET_GREET_SLOTS } from "@/lib/api";
import DatePicker from "@/components/DatePicker";

export default function MeetGreetPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    client_name: "",
    email: "",
    phone: "",
    pet_name: "",
    pet_type: "Dog",
    date: "",
    time_slot: "",
    address: "",
    notes: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.email || !form.phone || !form.pet_name || !form.date || !form.time_slot) {
      toast.error("Please complete your details and pick a date + time.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/meet-greet", form);
      setDone(data);
      toast.success("Meet & Greet requested! Aurora will confirm shortly. 💛");
    } catch {
      toast.error("Couldn't submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="mg-done" className="py-20">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 parchment-card rounded-3xl p-10 text-center">
          <CheckCircle2 className="w-14 h-14 text-[#4A6741] mx-auto" />
          <h2 className="font-display font-bold text-4xl text-[#3F2A56] mt-4">
            See you soon!
          </h2>
          <p className="text-[#5C4A73] mt-3 text-lg leading-relaxed">
            Thanks, <strong className="text-[#3F2A56]">{done.client_name}</strong>!
            Aurora will confirm your <strong>free Meet &amp; Greet</strong> for{" "}
            <strong>{new Date(done.date + "T00:00:00").toLocaleDateString()}</strong>{" "}
            at <strong>{done.time_slot}</strong> by phone or email shortly.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              data-testid="mg-back-home"
              className="rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-3"
            >
              Back to home
            </button>
            <button
              onClick={() => navigate("/book")}
              data-testid="mg-go-book"
              className="rounded-full border-2 border-[#3F2A56] text-[#3F2A56] font-semibold px-6 py-3 hover:bg-[#3F2A56]/5"
            >
              Browse services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="meet-greet-page" className="py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <header className="text-center mb-12">
          <span className="font-script text-2xl text-[#b85b7e]">free &amp; no obligation</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#3F2A56] mt-2 leading-tight">
            Let&rsquo;s have a Meet &amp; Greet.
          </h1>
          <p className="mt-4 text-[#5C4A73] text-lg max-w-2xl mx-auto leading-relaxed">
            Before any first visit, I love to meet you and your pet so everyone
            feels at home. Pick a time that works — no payment, no pressure.
          </p>
        </header>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
          <aside className="parchment-card rounded-3xl p-7 space-y-5 sticky top-28">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#3F2A56] text-[#F4E8C8] grid place-items-center">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <div className="font-script text-xl text-[#b85b7e]">what to expect</div>
                <h3 className="font-display text-2xl font-semibold text-[#3F2A56]">
                  About 20–30 minutes
                </h3>
              </div>
            </div>
            <ul className="space-y-3 text-[#5C4A73]">
              <Bullet>I come to your home so your pet stays in their comfort zone.</Bullet>
              <Bullet>We chat routines, treats, quirks, and any special needs.</Bullet>
              <Bullet>You get to see how I interact with your pet — no commitment required.</Bullet>
              <Bullet>You&rsquo;ll leave with a clear care plan and quote.</Bullet>
            </ul>
            <div className="rounded-2xl bg-[#E8F0E6] border-2 border-[#4A6741]/30 p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-[#4A6741] shrink-0 mt-0.5" />
              <p className="text-sm text-[#2A1C3A]">
                Grayed-out dates are days Aurora is unavailable. Pick another date and
                we&rsquo;ll wag from there!
              </p>
            </div>
          </aside>

          <form
            onSubmit={onSubmit}
            data-testid="meet-greet-form"
            className="parchment-card rounded-3xl p-7 sm:p-10 space-y-7"
          >
            <Section title="About you">
              <Grid>
                <Field label="Your name" required>
                  <input
                    data-testid="mg-input-name"
                    required
                    value={form.client_name}
                    onChange={(e) => update("client_name", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid="mg-input-phone"
                    required
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="mg-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Home address (optional)">
                  <input
                    data-testid="mg-input-address"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </Grid>
            </Section>

            <Section title="About your pet">
              <Grid>
                <Field label="Pet name" required>
                  <input
                    data-testid="mg-input-pet"
                    required
                    value={form.pet_name}
                    onChange={(e) => update("pet_name", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Pet type">
                  <select
                    data-testid="mg-input-pet-type"
                    value={form.pet_type}
                    onChange={(e) => update("pet_type", e.target.value)}
                    className={inputCls}
                  >
                    <option>Dog</option>
                    <option>Cat</option>
                    <option>Multiple pets</option>
                    <option>Other</option>
                  </select>
                </Field>
              </Grid>
            </Section>

            <Section title="Pick a day & time">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Date" required>
                  <DatePicker
                    testid="mg-date"
                    value={form.date}
                    onChange={(v) => update("date", v)}
                    placeholder="Pick a date"
                  />
                </Field>
                <Field label="Time slot" required>
                  <div className="grid grid-cols-4 gap-2" data-testid="mg-slots">
                    {MEET_GREET_SLOTS.map((slot) => (
                      <button
                        type="button"
                        key={slot}
                        data-testid={`mg-slot-${slot.replace(/[^0-9A-Za-z]/g, "")}`}
                        onClick={() => update("time_slot", slot)}
                        className={`px-2 py-2.5 text-sm rounded-xl border-2 transition-all font-semibold ${
                          form.time_slot === slot
                            ? "bg-[#3F2A56] text-[#F4E8C8] border-[#3F2A56]"
                            : "bg-[#F4E8C8]/60 border-[#3F2A56]/20 text-[#3F2A56] hover:border-[#3F2A56]/50"
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <Field label="Anything else?">
                <textarea
                  data-testid="mg-input-notes"
                  rows={3}
                  placeholder="Allergies, fears, favorite treats…"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </Section>

            <button
              type="submit"
              data-testid="mg-submit"
              disabled={submitting}
              className="w-full rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-4 text-base transition-all hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <PawPrint className="w-4 h-4" />
              {submitting ? "Sending…" : "Request my free Meet & Greet"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/20 px-4 py-3 text-[#2A1C3A] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-[#4A6741] transition-colors placeholder:text-[#5C4A73]/50";

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-display text-2xl font-semibold text-[#3F2A56] flex items-center gap-2">
        <Heart className="w-4 h-4 fill-[#b85b7e] text-[#b85b7e]" />
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#2A1C3A] mb-1.5">
        {label} {required && <span className="text-[#b85b7e]">*</span>}
      </span>
      {children}
    </label>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex gap-2.5">
      <PawPrint className="w-4 h-4 text-[#3F2A56]/70 shrink-0 mt-1" />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
